import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";

// iter256 — map "خدمات أخرى" activity slug to the closest
// platform_categories key so ?activity=X preselects a sensible default
// in the category dropdown when the user arrives from a partner CTA.
const ACTIVITY_TO_CATEGORY = {
  delivery: "transport_company",
  car_wash: "car_wash",
  workshop: "workshop",
  insurance: "insurance_company",
  financing: "bank",
  parts: "parts_store",
  tire_battery: "tire_battery_shop",
  periodic_inspection: "periodic_inspection",
  pre_purchase_inspection: "inspection_center",
};

export default function BusinessRegisterPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [qs] = useSearchParams();
  const activity = qs.get("activity");
  const preselect = activity ? (ACTIVITY_TO_CATEGORY[activity] || "dealer") : "dealer";

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name_ar: "", name_en: "", category: preselect });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiClient.get("/platform/categories").then(({ data }) => setCategories(data.items || []));
  }, []);

  if (user === undefined) return null; // wait for auth to hydrate
  if (!user) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/platform/businesses", form);
      toast.success(
        lang === "ar"
          ? "تم إنشاء المسودة. الخطوة التالية: ارفع الوثائق للتوثيق."
          : "Draft created. Next: upload documents for verification."
      );
      // Phase 2F — route straight to Verification so the user completes
      // the required documents before the workspace is publicly usable.
      navigate(`/business/${data.business.id}/verification`);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.ar || err?.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-xl mx-auto px-5 sm:px-8 py-10" data-testid="business-register">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#8a6f2e] font-semibold">
        {lang === "ar" ? "تسجيل نشاط تجاري" : "Register Business"}
      </div>
      <h1 className="text-3xl font-display font-bold text-[#0a0a0a] mt-1 mb-3">
        {lang === "ar" ? "ابدأ مساحة العمل الخاصة بك" : "Start your workspace"}
      </h1>
      <div className="text-[13px] rounded-xl bg-[#fef7e6] border border-[#c9a55c]/25 text-[#8a6f2e] px-4 py-3 mb-6" data-testid="biz-register-notice">
        {lang === "ar"
          ? "إنشاء النشاط هو بداية طلب التسجيل فقط. بعده ترفع السجل التجاري والمستندات اللازمة، ثم يراجعها Super Admin قبل التوثيق والتفعيل."
          : "Creating a workspace only starts the application. You'll then upload your CR and documents; a Super Admin reviews before activation."}
      </div>

      <form onSubmit={submit} className="space-y-4 rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-6">
        <label className="block">
          <div className="text-[11px] font-semibold text-[#525252] mb-1">{lang === "ar" ? "اسم النشاط (عربي)" : "Business Name (Arabic)"}</div>
          <input required value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                 className="w-full px-3 py-2.5 rounded-lg border border-[#0a0a0a]/15 text-sm"
                 data-testid="biz-name-ar" />
        </label>
        <label className="block">
          <div className="text-[11px] font-semibold text-[#525252] mb-1">{lang === "ar" ? "الاسم بالإنجليزي (اختياري)" : "Business Name (English, optional)"}</div>
          <input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                 className="w-full px-3 py-2.5 rounded-lg border border-[#0a0a0a]/15 text-sm" dir="ltr"
                 data-testid="biz-name-en" />
        </label>
        <label className="block">
          <div className="text-[11px] font-semibold text-[#525252] mb-1">{lang === "ar" ? "فئة النشاط" : "Business Category"}</div>
          <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#0a0a0a]/15 text-sm bg-white"
                  data-testid="biz-category">
            {categories.map((c) => (
              <option key={c.key} value={c.key}>{lang === "ar" ? c.label_ar : c.label_en}</option>
            ))}
          </select>
        </label>
        <div className="text-[12px] text-[#8a8a8a] pt-2">
          {lang === "ar"
            ? "بعد التسجيل ستحصل على لوحة تحكم كاملة + إمكانية توثيق النشاط + إدارة الموظفين والفروع."
            : "After registration you'll get a full dashboard + verification + employees & branches management."}
        </div>
        <div className="pt-2">
          <button type="submit" disabled={submitting}
                  className="w-full py-3 rounded-full text-sm font-bold shadow-sm disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                  data-testid="btn-submit-biz">
            {submitting ? "…" : (lang === "ar" ? "تسجيل النشاط" : "Register")}
          </button>
        </div>
      </form>
    </div>
  );
}
